/**
 * 50/50 power-up shop.
 *
 * Visual structure mirrors the eraser shop:
 *   - Blue-tinted balance hero card with the inventory count.
 *   - Blue-bordered explainer strip.
 *   - Wallet strip showing the cash + bonus split + "purchases spend
 *     bonus first" note.
 *   - "Buy 50/50s" list with three bundles; the cheapest-per-unit one
 *     gets a "POPULAR" badge + outlined card. Bundles show savings vs.
 *     the 1-pack unit price.
 *   - Footer note pointing out that 50/50s can also be earned via
 *     referrals + finishing inside a quiz's reward range (the launch
 *     promise is that you can still earn them — the buy flow is a
 *     top-up convenience, not a replacement).
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

// 50/50 glyph — half-filled circle matching the icon registry's
// `fiftyfifty` style. Inlined here so the hero card can render at 26px
// without depending on the icon helper's sizing.
const FIFTY_SVG = `
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9"></circle>
    <line x1="12" y1="3" x2="12" y2="21"></line>
    <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none"></path>
  </svg>
`;

const ACCENT = '#7FB4FF';            // 50/50 blue used elsewhere for the chip
const ACCENT_BG = 'rgba(127,180,255,0.12)';
const ACCENT_BORDER = 'rgba(127,180,255,0.42)';

export async function renderFiftyFifty(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/invite" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">50/50 power-up</div>
    </div>
    <div class="pad" id="ff-body">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  const host = document.getElementById('ff-body');
  let products;
  let balance;
  let wallet;
  try {
    const [bal, prods, w] = await Promise.all([
      api('/fiftyfifty/balance'),
      api('/fiftyfifty/products'),
      api('/wallet'),
    ]);
    balance = bal.fiftyfifty_balance;
    products = prods.items;
    wallet = w;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  const popularId = products
    .filter((p) => p.count > 1)
    .reduce(
      (best, p) => (best == null || p.unit_price_kobo < best.unit_price_kobo ? p : best),
      null,
    )?.id;

  const single = products.find((p) => p.count === 1);
  const singleUnit = single ? single.price_kobo : null;

  host.innerHTML = `
    <div class="card" style="padding:24px 18px;background:radial-gradient(120% 100% at 50% 0%, rgba(127,180,255,0.18) 0%, rgba(20,42,72,0.55) 50%, rgba(21,16,43,0.85) 100%);border:1px solid ${ACCENT_BORDER};text-align:center;">
      <div style="display:inline-flex;align-items:center;justify-content:center;width:64px;height:64px;background:${ACCENT_BG};border:1px solid ${ACCENT_BORDER};border-radius:14px;color:${ACCENT};">
        ${FIFTY_SVG}
      </div>
      <div style="margin-top:14px;font-size:12px;letter-spacing:0.18em;text-transform:uppercase;color:rgba(127,180,255,0.9);font-weight:700;">
        Your 50/50s
      </div>
      <div id="ff-balance" style="font-family:var(--font-display);font-size:46px;font-weight:800;color:${ACCENT};line-height:1.05;margin-top:6px;">
        ${balance}
      </div>
    </div>

    <div class="card" style="padding:14px 16px;margin-top:12px;background:transparent;border:1px solid ${ACCENT_BORDER};color:${ACCENT};font-size:13px;line-height:1.5;">
      A 50/50 removes two wrong answers in a live game.
      Use up to 2 per game.
    </div>

    <div id="ff-wallet-strip" class="card" style="padding:12px 14px;margin-top:10px;">
      <div class="row" style="gap:12px;align-items:center;">
        <div class="grow" style="min-width:0;">
          <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;">Wallet</div>
          <div style="font-family:var(--font-display);font-size:18px;color:#fff;font-weight:700;line-height:1.15;margin-top:2px;" id="ff-wallet-cash">
            ${escape(formatKobo(wallet.balance_kobo))}
          </div>
        </div>
        <div id="ff-wallet-bonus" style="text-align:right;">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#E94BA2;font-weight:700;">🎁 Bonus</div>
          <div style="font-family:var(--font-display);font-size:18px;color:#fff;font-weight:700;line-height:1.15;margin-top:2px;" id="ff-wallet-bonus-amount">—</div>
        </div>
      </div>
      <div class="muted" style="font-size:11px;line-height:1.45;margin-top:8px;">
        Purchases spend bonus first, then cash.
      </div>
    </div>

    <h3 class="sec" style="margin-top:18px;">Buy 50/50s</h3>
    <div id="ff-products" style="display:grid;gap:10px;">
      ${products.map((p) => renderProductCard(p, p.id === popularId, singleUnit)).join('')}
    </div>

    <div class="muted" style="font-size:11.5px;line-height:1.5;margin-top:14px;text-align:left;">
      Paid from your wallet · You can also <a href="/invite" data-link style="color:${ACCENT};">earn 50/50s by referring friends</a> or finishing in a live game's reward positions.
    </div>
  `;

  renderWalletStrip(wallet);

  document.querySelectorAll('[data-buy]').forEach((btn) => {
    btn.addEventListener('click', () => buy(btn.dataset.buy));
  });
}

function renderWalletStrip(wallet) {
  const cashEl = document.getElementById('ff-wallet-cash');
  if (cashEl) cashEl.textContent = formatKobo(wallet.balance_kobo);
  const bonusAmt = document.getElementById('ff-wallet-bonus-amount');
  if (bonusAmt) bonusAmt.textContent = formatKobo(Number(wallet.bonus_balance_kobo || 0));
}

function renderProductCard(p, isPopular, singleUnit) {
  const labelN = p.count === 1 ? '1 × 50/50' : `${p.count} × 50/50`;
  let savingsKobo = 0;
  if (singleUnit && p.count > 1) {
    savingsKobo = Math.max(0, singleUnit * p.count - p.price_kobo);
  }
  const savingsLine = savingsKobo > 0
    ? `<div style="font-size:11.5px;font-weight:700;color:#2BB39B;margin-top:2px;letter-spacing:0.03em;">SAVE ${escape(formatKobo(savingsKobo))}</div>`
    : '';

  const popularBadge = isPopular
    ? `<span style="display:inline-flex;align-items:center;padding:4px 9px;background:#1B2640;border:1px solid #2c3d62;border-radius:6px;font-size:10px;font-weight:800;color:${ACCENT};letter-spacing:0.10em;font-family:var(--font-display);text-transform:uppercase;margin-right:6px;">
         Popular
       </span>`
    : '';

  const cardStyle = isPopular
    ? `padding:14px;background:var(--surf-2);border:1.5px solid ${ACCENT_BORDER};box-shadow:0 0 0 1px rgba(127,180,255,0.10);`
    : 'padding:14px;background:var(--surf-2);';

  return `
    <div class="card" style="${cardStyle}">
      <div class="row" style="align-items:center;gap:12px;">
        <div style="width:42px;height:42px;flex-shrink:0;background:#15233F;border:1px solid ${ACCENT_BORDER};border-radius:10px;display:flex;align-items:center;justify-content:center;color:${ACCENT};">
          ${FIFTY_SVG}
        </div>
        <div class="grow" style="min-width:0;">
          <div style="font-size:16px;color:#fff;font-weight:700;">${escape(labelN)}</div>
          ${savingsLine}
        </div>
        ${popularBadge}
        <button type="button" class="btn sm" data-buy="${escape(p.id)}"
                style="white-space:nowrap;min-width:88px;text-align:center;font-weight:800;background:${ACCENT};color:#0A1830;border-bottom-color:#3A6BA3;">
          ${escape(formatKobo(p.price_kobo))}
        </button>
      </div>
    </div>
  `;
}

async function buy(productId) {
  const btn = document.querySelector(`[data-buy="${CSS.escape(productId)}"]`);
  if (!btn) return;
  if (!confirm('Purchase 50/50s? Your wallet will be charged.')) return;
  btn.disabled = true;
  const original = btn.textContent;
  btn.textContent = 'Buying…';
  try {
    const idempotency_key = `ff-buy-${productId}-${crypto.randomUUID()}`;
    const res = await api('/fiftyfifty/purchase', {
      method: 'POST',
      body: { product_id: productId, idempotency_key },
    });
    document.getElementById('ff-balance').textContent = res.fiftyfifty_balance;
    toast.success(`Got ${res.count} × 50/50!`);
    const me = auth.getMe();
    if (me) auth.setMe({ ...me, fiftyfifty_balance: res.fiftyfifty_balance });
    // Refresh the wallet strip so bonus-drains-first is visible: if the
    // user had ₦300 bonus and just spent ₦250, they should now see
    // ₦50 bonus + unchanged cash.
    try {
      const w = await api('/wallet');
      renderWalletStrip(w);
    } catch {
      // Soft-fail: the purchase succeeded; a stale strip is cosmetic.
    }
  } catch (err) {
    toast.fromError(err);
  } finally {
    btn.disabled = false;
    btn.textContent = original;
  }
}
